import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createGlobalState, useMap, useMount, usePrevious, useUpdateEffect } from 'react-use';
import { mapValues, sortBy } from 'lodash-es';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  isPast,
  isSameDay,
  isSameMonth,
  isToday,
  isValid,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {
  ResourceReservationsToDayMapsType,
  SchedulerDayColumn,
  SchedulerResource,
  SchedulerViewType,
} from '@lp/features/Scheduler/types';
import { getFilteredResources, getReservationsToDayMap, toZonedISODateKey } from '@lp/features/Scheduler/utils';
import { SchedulerControlFilters } from '@lp/core/store/scheduler';
import { EmployeesReservationsData } from '@/api/apis/resources';
import {
  BaseResource,
  BaseShift,
  MonthSchedule,
  PlanningProcessStatus,
  ResourcesWithShifts,
} from '@/api/apis/resources/schedule';
import { formatDate, getFullName } from '@/utils/helpers';

export const useScheduleMutationsLoader = createGlobalState(false);

/**
 * Configuration to override the default calendar-month navigation model.
 * Defaults preserve month-based behavior for all schedulers. The CS schedule
 * injects 28-day period providers (see `useCSScheduleControls`).
 */
export type ScheduleControlsConfig = {
  getStartDay?: (date: Date) => Date;
  getDateRange?: (date: Date) => { from: Date; to: Date };
  getPrevDate?: (date: Date) => Date;
  getNextDate?: (date: Date) => Date;
};

const defaultGetDateRange = (date: Date) => ({
  from: startOfWeek(startOfMonth(date)),
  to: endOfWeek(endOfMonth(date)),
});
const defaultGetPrevDate = (date: Date) => addMonths(date, -1);
const defaultGetNextDate = (date: Date) => addMonths(date, 1);

export const useScheduleControls = (config: ScheduleControlsConfig = {}) => {
  const {
    getStartDay = startOfMonth,
    getDateRange = defaultGetDateRange,
    getPrevDate = defaultGetPrevDate,
    getNextDate = defaultGetNextDate,
  } = config;
  const [searchParams, setSearchParams] = useSearchParams();
  const daySearchParam = searchParams.get('day');
  const initialDay = daySearchParam ? parse(daySearchParam, 'MM-dd-yyyy', new Date()) : new Date();
  const [selectedDate, setSelectedDate] = useState(startOfDay(isValid(initialDay) ? initialDay : new Date()));
  const [isReservationsView, setIsReservationsView] = useState(
    searchParams.get('view') === SchedulerViewType.Reservations,
  );
  const startDay = useMemo(() => getStartDay(selectedDate), [selectedDate, getStartDay]);
  const selectedDateRange = useMemo(() => getDateRange(selectedDate), [selectedDate, getDateRange]);
  const daysInterval = useMemo(() => {
    return eachDayOfInterval({
      start: selectedDateRange.from,
      end: selectedDateRange.to,
    });
  }, [selectedDateRange]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        prev.set('day', formatDate(selectedDate, 'MM-dd-yyyy'));
        return prev;
      },
      { replace: true },
    );
  }, [selectedDate, setSearchParams]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (isReservationsView) prev.set('view', SchedulerViewType.Reservations);
        else prev.delete('view');

        return prev;
      },
      { replace: true },
    );
  }, [isReservationsView, setSearchParams]);

  const onMonthPrev = useCallback(() => setSelectedDate(getPrevDate(selectedDate)), [selectedDate, getPrevDate]);
  const onMonthNext = useCallback(() => setSelectedDate(getNextDate(selectedDate)), [selectedDate, getNextDate]);

  return {
    startDay,
    selectedDate,
    selectedDateRange,
    daysInterval,
    onMonthPrev,
    onMonthNext,
    onDateChange: setSelectedDate,
    isReservationsView,
    setIsReservationsView,
  };
};

export const useDayColumns = (
  daysInterval: Date[],
  selectedDate: Date,
  isSameWindow: (day: Date, selectedDate: Date) => boolean = isSameMonth,
) => {
  return useMemo<SchedulerDayColumn[]>(() => {
    return daysInterval.map((d) => {
      return {
        key: toZonedISODateKey(d),
        date: d,
        label: formatDate(d, 'EEE d, MMM'),
        isToday: isToday(d),
        isPast: isPast(d),
        isSelectedDay: isSameDay(d, selectedDate),
        isCurrentMonth: isSameWindow(d, selectedDate),
      };
    });
  }, [daysInterval, selectedDate, isSameWindow]);
};

const USER_COL_WIDTH = 220;

export const useSchedulerTableAutoScroll = (selectedDate: Date) => {
  const prevSelectedDate = usePrevious(selectedDate);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rowsMapRef = useRef<Map<string, HTMLTableRowElement | null>>(new Map());
  const colsMapRef = useRef<Map<string, HTMLTableCellElement | null>>(new Map());

  const rowRefCallback = useCallback((resourceId: string, node: HTMLTableRowElement | null) => {
    rowsMapRef.current.set(resourceId, node);
  }, []);

  const columnRefCallback = useCallback((node: HTMLTableCellElement | null, date: Date) => {
    colsMapRef.current.set(date.toISOString(), node);
  }, []);

  const scrollToDayColumn = useCallback((day: Date, isSmooth = false) => {
    const colEl = colsMapRef.current.get(day.toISOString());
    if (scrollContainerRef.current && colEl) {
      const left = colEl.offsetLeft - (USER_COL_WIDTH + colEl.clientWidth);
      const behavior = isSmooth ? 'smooth' : 'instant';
      scrollContainerRef.current.scrollTo({ left, behavior });
    }
  }, []);

  const scrollToResourceRow = useCallback((resourceId: string, isSmooth = false) => {
    const rowEl = rowsMapRef.current.get(resourceId);
    const behavior = isSmooth ? 'smooth' : 'instant';
    rowEl?.scrollIntoView({ behavior, block: 'center' });
  }, []);

  const scrollToCell = useCallback(
    (resourceId: string, date: Date, isSmooth = false) => {
      scrollToResourceRow(resourceId, isSmooth);
      scrollToDayColumn(date, isSmooth);
    },
    [scrollToDayColumn, scrollToResourceRow],
  );

  useMount(() => {
    scrollToDayColumn(selectedDate);
  });

  useUpdateEffect(() => {
    const isSmooth = prevSelectedDate ? isSameMonth(prevSelectedDate, selectedDate) : false;
    scrollToDayColumn(selectedDate, isSmooth);
  }, [selectedDate]);

  return {
    scrollContainerRef,
    rowRefCallback,
    columnRefCallback,
    scrollToResourceRow,
    scrollToDayColumn,
    scrollToCell,
  };
};

export const useMonthScheduleStates = (monthSchedule?: MonthSchedule, isPlanningMode?: boolean, readonly?: boolean) => {
  const { hasSchedule, hasPreSchedule, hasPlanningProcess, planningProcessStatus } = monthSchedule ?? {};
  const isUnplannedPreSchedule = !!hasPreSchedule && !hasSchedule && !hasPlanningProcess;
  const isResettable = !readonly && (isPlanningMode || isUnplannedPreSchedule);
  const isEditable = !readonly && (isPlanningMode || !!hasSchedule || isUnplannedPreSchedule);
  const isGenerating = !readonly && isPlanningMode && planningProcessStatus === PlanningProcessStatus.Generating;

  return {
    isEditable,
    isGenerating,
    isResettable,
    isUnplannedPreSchedule,
  };
};

const defaultFilterComparator = <T extends string, S extends BaseShift<T>>(dayFilter: Set<T>, shift?: S) => {
  return !!shift && dayFilter.has(shift.shiftType);
};

type UseSchedulerUserRowsProps<R extends BaseResource, T extends string, S extends BaseShift<T>> = {
  dayCols: SchedulerDayColumn[];
  userList: R[];
  shiftsData: ResourcesWithShifts<R, S>;
  filters: SchedulerControlFilters;
  filterComparator?: (dayFilter: Set<T>, shift?: S) => boolean;
};

export const useSchedulerUserRows = <R extends SchedulerResource, T extends string, S extends BaseShift<T>>({
  dayCols,
  userList,
  shiftsData,
  filters,
  filterComparator = defaultFilterComparator,
}: UseSchedulerUserRowsProps<R, T, S>) => {
  const [sortAsc, setSortAsc] = useState<boolean>(true);
  const [daysFilters, { set: setDayFilter, reset: resetDaysFilters }] = useMap<Record<string, Set<T>>>();

  useEffect(resetDaysFilters, [dayCols, resetDaysFilters]);

  const filteredUserList = useMemo(() => {
    const daysFilterEntries = Object.entries(daysFilters).filter(
      ([d, f]) => dayCols.some((col) => col.key === d) && f.size > 0,
    );
    const { statusFilters: withFilters } = filters;
    const resultList = getFilteredResources(userList, filters);

    if (!withFilters || daysFilterEntries.length === 0) return resultList;

    return resultList.filter((user) => {
      return daysFilterEntries.every(([day, filter]) => {
        return filterComparator(filter, shiftsData[user?.id]?.days[day]);
      });
    });
  }, [daysFilters, filters, userList, dayCols, filterComparator, shiftsData]);

  const sortedUserList = useMemo(() => {
    if (sortAsc === undefined) return filteredUserList;

    const sorted = sortBy(filteredUserList, (u) => getFullName(u?.firstName, u?.lastName));
    return sortAsc ? sorted : sorted.reverse();
  }, [filteredUserList, sortAsc]);

  const toggleSortAsc = useCallback(() => {
    setSortAsc((val) => !val);
  }, []);

  return {
    userRows: sortedUserList,
    sortAsc,
    toggleSortAsc,
    daysFilters,
    setDayFilter,
  };
};

export const useResourceReservationsToDayMaps = (reservationsData?: EmployeesReservationsData['employees']) => {
  return useMemo<ResourceReservationsToDayMapsType>(() => {
    return mapValues(reservationsData, getReservationsToDayMap);
  }, [reservationsData]);
};
